/**
 * Base class for anything that can supply waveform peaks to the renderer.
 *
 * Two implementations: LocalWaveform (peaks generated from a decoded
 * AudioBuffer) and RemoteWaveform (peaks fetched from the af backend). The
 * renderer talks only to this interface and never knows which it has.
 */

export class WaveformSource {
  constructor() {
    /** @type {Map<number, import('./PeakData.js').PeakLevel>} keyed by samplesPerPeak */
    this.levels = new Map();
    /** Ascending samples-per-peak — finest first. */
    this.levelSizes = [];
    /** Called when new peak data lands, so the host can schedule a redraw. */
    this.onChange = null;
  }

  /**
   * Choose the level to draw at a given zoom.
   *
   * The finest level whose peaks are no wider than a pixel, i.e. the largest
   * samplesPerPeak that still fits within samplesPerPixel. Levels are decades,
   * so this lands between 1 and 10 peaks per pixel.
   *
   * (The previous implementation multiplied by an "aggressive factor" of 2 and
   * picked the first level *above* the threshold, which selected a level up to
   * 10× too coarse in the middle of each decade — visible as a blocky waveform
   * between roughly 200 and 1000 samples per pixel.)
   */
  selectLevel(samplesPerPixel) {
    let chosen = this.levelSizes[0];
    for (const samplesPerPeak of this.levelSizes) {
      if (samplesPerPeak > samplesPerPixel) break;
      chosen = samplesPerPeak;
    }
    return chosen;
  }

  /**
   * Peaks to draw for a sample range, plus a request for anything better that
   * isn't resident yet.
   *
   * Returns the finest level that is fully resident for the range, which is not
   * necessarily the ideal one — that's what keeps the waveform from blanking
   * while blocks are in flight. It sharpens on a later frame once they land.
   *
   * @returns {{level: PeakLevel, startPeak: number, endPeak: number}|null}
   */
  getView(startSample, endSample, samplesPerPixel) {
    if (this.levelSizes.length === 0 || endSample <= startSample) return null;

    const ideal = this.selectLevel(samplesPerPixel);
    this.request(ideal, startSample, endSample);

    // Walk from the ideal level toward coarser ones. The coarsest is always
    // fully resident, so this terminates with something to draw.
    const from = this.levelSizes.indexOf(ideal);
    for (let i = Math.max(0, from); i < this.levelSizes.length; i++) {
      const samplesPerPeak = this.levelSizes[i];
      const level = this.levels.get(samplesPerPeak);
      if (!level) continue;

      const startPeak = Math.max(0, Math.floor(startSample / samplesPerPeak));
      const endPeak = Math.min(level.count, Math.ceil(endSample / samplesPerPeak));
      if (endPeak <= startPeak) continue;

      if (level.isResident(startPeak, endPeak)) {
        return { level, startPeak, endPeak };
      }
    }

    return null;
  }

  /**
   * Ask for peaks covering a range at a level. No-op when everything is already
   * in memory; RemoteWaveform overrides it to schedule fetches.
   */
  request() {}

  /** Total resident peak bytes, for diagnostics. */
  get residentBytes() {
    let bytes = 0;
    for (const level of this.levels.values()) bytes += level.residentBytes;
    return bytes;
  }

  destroy() {
    this.onChange = null;
    this.levels.clear();
  }

  /** @protected Register a level and keep levelSizes sorted finest-first. */
  addLevel(level) {
    this.levels.set(level.samplesPerPeak, level);
    this.levelSizes = [...this.levels.keys()].sort((a, b) => a - b);
  }
}
